// @generated-by docnow-change-password-modal v2
// ============================================================
// CHANGE PASSWORD MODAL
// ============================================================
// Self-contained modal for logged-in admin to change their own password.
// Renders nothing when open is false. Calls authAPI.changePassword.
// On success: shows confirmation, auto-closes after 1.5s.
// ============================================================
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { authAPI } from '../services/api';

export default function ChangePasswordModal({ open, onClose }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
      setError('');
      setSuccess(false);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  // Password complexity matches backend rule:
  // >=8 chars, >=1 lowercase, >=1 uppercase, >=1 digit
  const meetsComplexity = (pwd) =>
    pwd.length >= 8 &&
    /[a-z]/.test(pwd) &&
    /[A-Z]/.test(pwd) &&
    /\d/.test(pwd);

  const canSubmit =
    currentPassword.length > 0 &&
    meetsComplexity(newPassword) &&
    newPassword === confirmPassword &&
    !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPassword) { setError('Συμπληρώστε τον τρέχοντα κωδικό'); return; }
    if (!meetsComplexity(newPassword)) {
      setError('Ο νέος κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες, κεφαλαία, πεζά και αριθμό');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Οι δύο κωδικοί δεν ταιριάζουν');
      return;
    }
    if (newPassword === currentPassword) {
      setError('Ο νέος κωδικός πρέπει να είναι διαφορετικός από τον τρέχοντα');
      return;
    }

    setSubmitting(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setSuccess(true);
      toast.success('Ο κωδικός άλλαξε επιτυχώς');
      setTimeout(() => onClose && onClose(), 1500);
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.error) || 'Αποτυχία αλλαγής κωδικού';
      setError(msg);
      setSubmitting(false);
    }
  };

  const eyeBtnStyle = {
    position: 'absolute', right: 12, top: '50%',
    transform: 'translateY(-50%)',
    background: 'none', border: 'none',
    fontSize: 18, cursor: 'pointer', color: '#8A97A8',
  };

  const inputStyle = {
    width: '100%', padding: 10, paddingRight: 44,
    borderRadius: 8, border: '1px solid #E8ECF2',
    fontSize: 14, fontFamily: 'inherit',
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose && onClose(); }}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 31, 61, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2000,
      }}
    >
      <div style={{
        background: 'white', borderRadius: 12,
        padding: 28, maxWidth: 480, width: '90%',
        boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        position: 'relative',
      }}>
        <button
          onClick={() => !submitting && onClose && onClose()}
          aria-label="Close"
          style={{
            position: 'absolute', right: 12, top: 12,
            background: 'none', border: 'none',
            fontSize: 22, cursor: 'pointer', color: '#8A97A8',
          }}
        >×</button>

        <h2 style={{ margin: '0 0 6px 0', fontSize: 20, color: '#0F1F3D' }}>
          Αλλαγή Κωδικού
        </h2>
        <p style={{ margin: '0 0 20px 0', fontSize: 13, color: '#8A97A8' }}>
          Ο νέος κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες, κεφαλαία, πεζά και αριθμό.
        </p>

        {success ? (
          <div style={{
            padding: 16, borderRadius: 8,
            background: '#D1FAE5', color: '#065F46',
            fontWeight: 600, textAlign: 'center',
          }}>
            Ο κωδικός άλλαξε επιτυχώς
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#0F1F3D' }}>
                Τρέχων κωδικός
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoFocus
                  disabled={submitting}
                  style={inputStyle}
                  placeholder="••••••••"
                />
                <button type="button" onClick={() => setShowCurrent((s) => !s)} style={eyeBtnStyle}>
                  {showCurrent ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#0F1F3D' }}>
                Νέος κωδικός
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={submitting}
                  style={inputStyle}
                  placeholder="τουλάχιστον 8 χαρακτήρες"
                />
                <button type="button" onClick={() => setShowNew((s) => !s)} style={eyeBtnStyle}>
                  {showNew ? '🙈' : '👁'}
                </button>
              </div>
              {newPassword && !meetsComplexity(newPassword) && (
                <div style={{ marginTop: 6, fontSize: 11, color: '#92400E' }}>
                  Δεν πληροί τις απαιτήσεις
                </div>
              )}
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#0F1F3D' }}>
                Επιβεβαίωση νέου κωδικού
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={submitting}
                  style={inputStyle}
                  placeholder="επαναλάβετε τον νέο κωδικό"
                />
                <button type="button" onClick={() => setShowConfirm((s) => !s)} style={eyeBtnStyle}>
                  {showConfirm ? '🙈' : '👁'}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <div style={{ marginTop: 6, fontSize: 11, color: '#991B1B' }}>
                  Δεν ταιριάζει
                </div>
              )}
            </div>

            {error && (
              <div style={{
                marginBottom: 16, padding: 10, borderRadius: 6,
                background: '#FEE2E2', color: '#991B1B', fontSize: 13,
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => !submitting && onClose && onClose()}
                disabled={submitting}
                style={{
                  background: 'transparent', color: '#475569',
                  border: '1px solid #E8ECF2', borderRadius: 8,
                  padding: '10px 18px', fontWeight: 600,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >Ακύρωση</button>
              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  background: canSubmit ? '#C0185A' : '#D1D5DB',
                  color: 'white', border: 'none', borderRadius: 8,
                  padding: '10px 18px', fontWeight: 600,
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                }}
              >{submitting ? 'Αλλαγή...' : 'Αλλαγή Κωδικού'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
